import { FormikProps } from 'formik'
import { ButtonGroupItem } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import {
  Contract,
  ContractTenant,
  ModifyContractInput,
} from '../../../../../common/validations'
import { TenantChoiceState } from '../../../ContractChanges/components/TenantChoice/TenantChoice'
import { lockChoiceByTenantStatus } from '../../../ContractChanges/components/TenantChoice/lockChoiceByTenantStatus'
import { useSuggestions } from '../../components/TenantSelector/utils/useSuggestions'
import { getValue } from '../../components/TenantSelector/utils/getValue'
import { TerminateSuggestionAction } from '../../../ContractChanges/hooks/useTerminateSuggestions'

type UseContractPartyChoiceParams = {
  contract: Contract
  tenants: ContractTenant[]
  movingOutTenantIds: number[]
  changeMovingOutTenants: (
    form: FormikProps<ModifyContractInput>,
    tenantIds: number[]
  ) => Promise<void>
}

export const useContractPartyChoice = ({
  contract,
  tenants,
  movingOutTenantIds,
  changeMovingOutTenants,
}: UseContractPartyChoiceParams) => {
  const { t } = useTranslation(['contractModification'])
  const { getSuggestions } = useSuggestions(contract)

  const resolveMovingOutTenantIds = (
    tenant: ContractTenant,
    suggestionId: string | undefined
  ) => {
    if (suggestionId === TerminateSuggestionAction.TERMINATE) {
      return [...movingOutTenantIds, tenant.id]
    }

    return tenant.id === contract.tenant.id
      ? []
      : movingOutTenantIds.filter((tenantId) => tenantId !== tenant.id)
  }

  const toTransferSuggestion = (suggestion: ButtonGroupItem) => {
    switch (suggestion.id) {
      case TerminateSuggestionAction.IN_EFFECT:
        return {
          ...suggestion,
          label: t('contractModification:tenantChoices.staysInApartment'),
        }
      case TerminateSuggestionAction.TERMINATE:
        return {
          ...suggestion,
          label: t('contractModification:tenantChoices.movesOut'),
        }
      default:
        return suggestion
    }
  }

  const resolveContractPartyChoice = (
    form: FormikProps<ModifyContractInput>,
    tenant: ContractTenant
  ): TenantChoiceState =>
    lockChoiceByTenantStatus(contract, tenants, tenant, {
      type: 'select',
      suggestions: getSuggestions(tenant, movingOutTenantIds).map(
        toTransferSuggestion
      ),
      value: getValue(contract, tenant, movingOutTenantIds.includes(tenant.id)),
      onChange: (suggestionId) =>
        changeMovingOutTenants(
          form,
          resolveMovingOutTenantIds(tenant, suggestionId)
        ),
    })

  return { resolveContractPartyChoice }
}
